import React, { ReactElement, useEffect, useState } from 'react';
import { Modal, Form, Row, Col, Radio, Button, Checkbox, Upload } from 'antd';
import { UploadChangeParam, UploadFile, UploadProps } from 'antd/lib/upload/interface';
import PatientSignUploadIcon from '../../../../../../assets/images/svg/Patient_Sign_Upload_Icon.svg';
import ConsentDeclarationIcon from '../../../../../../assets/images/svg/Consent_Icon.svg';
import {
  APPLICATION_ID, APPLICATION_ID_2, CASHLESS_FORM_CONSTANTS, FILE_EXCEED_MESSAGE,
  FILE_EXTENSION,
  getPatientConsentDeclaration,
  GUTTOR_SPACE, HOSPITAL_MAX_FILE_SIZE, HOSPITAL_SEAL_SUPPORTED_FORMATS, INVENTROY_CONSTANTS,
  PATIENT_SIGNATURE_CONSTANTS,
  patientConsentAgreement,
} from '../../../../../../shared/constants/commonConstants';
import { centeredNotification, formatDate } from '../../../../../../utils/utility';
import ApplicationDetail from '../../../../../../shared/services/applicationDetail.service';
import { ReactComponent as Plus_Icon } from '../../../../../../assets/images/svg/Plus_icon.svg';
import { ReactComponent as Delete_Icon } from '../../../../../../assets/images/svg/Patient_Consent_Delete_Icon.svg';
import { ReactComponent as Cross_Icon } from '../../../../../../assets/images/svg/cross-circle.svg';
import { ReactComponent as Cross_Filled_Icon } from '../../../../../../assets/images/svg/Cross_Red_Filled_Icon.svg';
import { useDispatch, useSelector } from 'react-redux';
import {
  setConsentDateTime, setPatientConsentProceedClicked, setPatientSignature, setSelectedConsentOptions,
  setSelectedConsentOptionStyle,
} from '../../../../../../store/actions/newAdmissionAction';
import PatientConsentSuccessModal from './PatientConsentSuccessModal';
import { getISTDateWithTimeSeconds } from '../../../../../../utils/dateTimeUtils';
import { ReactComponent as FileIcon } from '../../../../../../assets/images/svg/HIS_File_Icon.svg';
import Lottie from 'react-lottie';
import loader from '../../../../../../assets/images/lottie-json/lottie_loading.json';
import { bytesToMB, formatDateAndTime } from '../../../service/crmUtils';
import { fetchFileBytesData } from '../../../../../../store/actions/inventoryAction';
import { RootState } from '../../../../../../store';
import './ConsentSignature.less';
import { isEmpty } from 'lodash';

const { Dragger } = Upload;

const ConsentDeclarationModal = ({ isModalVisible, handleModalCancel, formValues, setIsDateSelected,
  isDateSelected, setIsSubmitBtnVisible, isSubmitBtnVisible, isChecked, setIsChecked,
  memberName, setShowRandomOption }: any): ReactElement => {
  const [form] = Form.useForm();
  const { newAdmission: { patientSignature: fileList, consentOption } }
    = useSelector((state: RootState): any => state);
  const [fileByte, setFileByte] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useDispatch();

  useEffect((): any => {
    if (!isSubmitBtnVisible) {
      form?.resetFields();
      setIsChecked(false);
    }
  }, [isSubmitBtnVisible]);

  useEffect((): any => {
    if (consentOption) {
      form.setFieldsValue({ consentSignature: consentOption });
    } else {
      form.setFieldsValue({ consentSignature: '' });
    }
  }, [consentOption]);

  useEffect((): any => {
    if (isModalVisible) {
      const uploadedFileGuid = fileList?.[0]?.response?.fileUploadDetails?.[0]?.fileGuid;
      getFileBytes(uploadedFileGuid);
    }
  }, [isModalVisible]);

  useEffect((): any => {
    // After file is uploaded, set the loader to show and then delay showing the image
    if (isLoading) {
      const timer = setTimeout((): any => {
        setIsLoading(false);
      }, 800);
      return (): any => clearTimeout(timer);
    }
  }, [isLoading]);

  const handleOptionChange = (e: any): void => {
    const selectedValue = e.target.value;
    localStorage?.removeItem(INVENTROY_CONSTANTS.patient_signature);
    dispatch(setSelectedConsentOptions(selectedValue));
    dispatch(setSelectedConsentOptionStyle({}));
    form.setFieldsValue({ consentSignature: selectedValue });
    dispatch(setPatientConsentProceedClicked(false));
    setIsChecked(false);
    dispatch(setPatientSignature([]));
    setIsDateSelected(null);
  };

  const handleCheckBox = (e: any): void => {
    setIsChecked(e.target.checked);
  };

  const getFileBytes = async (imgId: string): Promise<void> => {
    if (imgId) {
      const fileIdentifiers = [imgId];
      dispatch(fetchFileBytesData(APPLICATION_ID, fileIdentifiers, onSuccess));
    }
  };

  const onSuccess = (response: any): void => {
    setFileByte(response?.fileDetails[0]?.fileBytes);
  };

  const fileBeforeUpload = (file: File): boolean => {
    if (file?.size > HOSPITAL_MAX_FILE_SIZE) {
      centeredNotification(FILE_EXCEED_MESSAGE);
      return false;
    }

    if (!HOSPITAL_SEAL_SUPPORTED_FORMATS?.['fileFormats']?.includes(file?.type)) {
      centeredNotification(HOSPITAL_SEAL_SUPPORTED_FORMATS?.['fileErrorMessage']);
      return false;
    }
    return true;
  };

  const draggerItemRender = (): null => null;

  const draggerActions: UploadProps = {
    showUploadList: true,
    beforeUpload: fileBeforeUpload,
    multiple: true,
    action: process.env.REACT_APP_BASE_URL + 'File/Upload',
    // headers: { accessToken: ApplicationDetail.instance.getAccessToken() },
    withCredentials: true,
    onChange: async (info: UploadChangeParam<UploadFile<any>>): Promise<any> => {
      const { status, uid, response } = info?.file || {};
      if (status === 'done') {
        const { isSuccess, errorMessage, fileUploadDetails } = response || {};
        const formData = new FormData() as any;
        formData.append('FileUploadType', INVENTROY_CONSTANTS.patient_signature);
        if (isSuccess) {
          const uploadedOn = getISTDateWithTimeSeconds(new Date());
          const updatedList = [
            ...fileList,
            { ...info.file, uploadedOn },
          ];
          dispatch(setPatientSignature(updatedList));
          const fileGuid = fileUploadDetails?.[0]?.fileGuid;
          const fileModifiedOn = new Date()?.toISOString();
          const patientSign = { guid: fileGuid, modifiedOn: fileModifiedOn };
          localStorage?.setItem(INVENTROY_CONSTANTS.patient_signature, JSON?.stringify(patientSign));
          getFileBytes(fileUploadDetails?.[0]?.fileGuid);
          setIsDateSelected({ ...info.file, uploadedOn });
          setIsLoading(true);
        } else {
          centeredNotification(errorMessage);
        }
      } else if (status === 'removed') {
        const updatedList = fileList?.filter((f: UploadFile<any>): boolean => f.uid !== uid);
        dispatch(setPatientSignature(updatedList));
      }
    },
    fileList,
    data: (): Record<string, string> => {
      return {
        FileUploadType: INVENTROY_CONSTANTS.patient_signature,
        ApplicationId: APPLICATION_ID_2,
      };
    },
  };

  const showProceedButton =
    (form?.getFieldValue(['consentSignature']) === PATIENT_SIGNATURE_CONSTANTS.PATIENT_SIGNATURE_OPTIONS[0] &&
      fileList?.length > 0) ||
    form?.getFieldValue(['consentSignature']) === PATIENT_SIGNATURE_CONSTANTS.PATIENT_SIGNATURE_OPTIONS[1];

  const onProceed = (): void => {
    dispatch(setPatientConsentProceedClicked(true));
    handleModalCancel();
    setShowSuccessModal(true);
    const dateTimeFormat = formatDateAndTime(new Date(), consentOption !== '');
    dispatch(setConsentDateTime(dateTimeFormat));
  };

  const onRemove = (file: any): void => {
    const updatedList = fileList.filter((f: UploadFile<any>): boolean => f.uid !== file?.[0]?.uid);
    dispatch(setPatientSignature(updatedList));
    setIsChecked(false);
  };

  const renderUploadListItem = (actions: any, files: UploadFile<any>[]): ReactElement[] => {
    return files?.map((file: any, index: number): ReactElement => (
      <Row key={index} className="patient-sign-container-0">
        <Col md={5} className="patient-sign-img-container">
          <img className="patient-sign-image col-patient-sign-left-23 hospital-seal justify-self-center" src={`data:image/jpeg;base64,${fileByte}`} alt="Patient Sign Logo" />
        </Col>
        <Col md={7} className="file-name-col"><FileIcon className="fill-gray v-align-middle" /> &nbsp;<span className="patient-sign-file-name">{file?.name}</span></Col>
        <Col md={5} className="file-date-col">{formatDate(file?.lastModifiedDate)}</Col>
        <Col md={4} className="file-size-col">{bytesToMB(file?.size)}</Col>
        <Col md={2} className="file-delete-col"><Delete_Icon onClick={(): void => onRemove(files)} /></Col>
      </Row>
    ));
  };

  const defaultLottieOptions = {
    loop: true,
    autoplay: true,
    animationData: loader,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  const getConsentUploadButtonClass = (): string => {
    return `${form.getFieldValue('consentSignature') === PATIENT_SIGNATURE_CONSTANTS.PATIENT_SIGNATURE_OPTIONS[1]
      ? 'no-mt'
      : ''
      } patient-sign-upload-cont-btn`;
  };

  const getPatientSignModalClass = (
    consentOptionVal: string,
    isLoadingData: boolean,
    fileListData: any[],
  ): string => {
    const baseClass = 'patient-sign-modal custom-modal';
    if (isEmpty(consentOptionVal)) {
      return `${baseClass} patient-sign-modal-no-consent`;
    }

    if (consentOptionVal === PATIENT_SIGNATURE_CONSTANTS.PATIENT_SIGNATURE_OPTIONS[0]) {
      if (isLoadingData) {
        return `${baseClass} patient-sign-modal-loading-state`;
      }
      if (fileListData?.length > 0) {
        return `${baseClass} patient-sign-modal-has-files`;
      }
      return `${baseClass} patient-sign-upload-patient-modal`;
    }

    if (consentOption === PATIENT_SIGNATURE_CONSTANTS.PATIENT_SIGNATURE_OPTIONS[1]) {
      return `${baseClass} consent-panel-signature`;
    }

    return baseClass;
  };

  const getConsentUploadContainerClass = (
    consentOptionSelected: string,
    fileListValue: any[],
  ): string => {
    if (consentOptionSelected === '') {
      return 'consent-upload-container';
    }
    if (consentOptionSelected === PATIENT_SIGNATURE_CONSTANTS.PATIENT_SIGNATURE_OPTIONS[0]) {
      return fileListValue?.length > 0
        ? 'consent-modal-upload-sign-files-container'
        : 'consent-modal-upload-sign-container';
    }
    return 'consent-modal-consent-sign-container';
  };

  return (
    <><Modal
      visible={isModalVisible}
      title={PATIENT_SIGNATURE_CONSTANTS.CONSENT_MODAL_TITLE}
      onCancel={handleModalCancel}
      className={getPatientSignModalClass(consentOption, isLoading, fileList)}
      footer={null}
      centered={true}
      closeIcon={<Cross_Icon />}
      width={!isEmpty(fileList) ? 904 : 776}
    >
      <Form
        form={form}
        layout="vertical"
        className="consentSignatureOptions"
        initialValues={{ consentSignature: '' }}
      >
        <Row gutter={[16, 16]}
          className={getConsentUploadContainerClass(consentOption, fileList)}
        >
          <Col span={12} className="patient-sign-pd-l-28">
            <div className="text-center">
              <img
                src={PatientSignUploadIcon}
                alt={PATIENT_SIGNATURE_CONSTANTS.PATIENT_SIGNATURE_OPTIONS[0]}
                className="patient-sign-upload-modal-icon" />
              <Radio
                value={PATIENT_SIGNATURE_CONSTANTS.PATIENT_SIGNATURE_OPTIONS[0]}
                checked={form.getFieldValue('consentSignature') === 'Upload Patient Signature'}
                onChange={handleOptionChange}
                className="consent-radio-btn"
              >
                <span className="consent-radio-button-option">{PATIENT_SIGNATURE_CONSTANTS.PATIENT_SIGNATURE_OPTIONS[0]}</span>
              </Radio>
            </div>
          </Col>
          <Col span={12} className="patient-sign-pd-r-35px">
            <div className="text-center">
              <img
                src={ConsentDeclarationIcon}
                alt={PATIENT_SIGNATURE_CONSTANTS.PATIENT_SIGNATURE_OPTIONS[1]}
                className="patient-sign-upload-modal-icon" />
              <Radio
                value={PATIENT_SIGNATURE_CONSTANTS.PATIENT_SIGNATURE_OPTIONS[1]}
                checked={form.getFieldValue('consentSignature') === 'Informed Consent Via Hospital'}
                onChange={handleOptionChange}
                className="consent-radio-btn"
              >
                <span className="consent-radio-button-option patient-sign-left-12">
                  {PATIENT_SIGNATURE_CONSTANTS.PATIENT_SIGNATURE_OPTIONS[1]}</span>
              </Radio>
            </div>
          </Col>
        </Row>
      </Form>
      {form.getFieldValue('consentSignature') === PATIENT_SIGNATURE_CONSTANTS.PATIENT_SIGNATURE_OPTIONS[0] && (
        <div className="mt-md">
          <div>
            <p className="scan-upload-text">{PATIENT_SIGNATURE_CONSTANTS.UPLOAD_TEXT}</p>
          </div>
          <hr className="consent-panel-hr" />

          {(!isEmpty(fileList) && !isLoading) && <p className="prefilled-name">
            Patient Name : &nbsp;<b className="prefilled-name-bold">{memberName}</b></p>}
          {isLoading && <Row className="updated-seal-container">
            <Col md={6} className="display-flex">
              <FileIcon />
              <p className="uploaded-patient-sign">{CASHLESS_FORM_CONSTANTS.SIGNATURE}</p>
            </Col>
            {isLoading &&
              <Col md={16} className="lottie-icon-loader">
                <Lottie options={defaultLottieOptions} />
              </Col>}
            {isLoading && <Col md={2} className="cross-icon-filled">
              <Cross_Filled_Icon /></Col>}
          </Row>}
          <div className={!fileList?.length ? 'show-other-upload-flow-2' : `${!isLoading ? 'show-other-upload-flow-3' : 'display-none show-other-upload-flow-3'}`}>

            {(!fileList?.length && !isLoading) && <Dragger
              {...draggerActions}
              itemRender={draggerItemRender}
              id="dragger"
              className={`${isLoading ? '' : 'file-upload-dragger'}`}
              data-testid="dropzone"
              openFileDialogOnClick={true}
            >
              <div>
                <div className="flex flex-element flex-row HIS-dragger-item">
                  <div className="flex-element flex-col justify-center items-center w-90">
                    <b className="ant-upload-text his-file-upload-text"> Drag & Drop your file(s) here.
                    </b>
                    <p className="ant-upload-hint w-90">
                      <span className="allowed-files">{PATIENT_SIGNATURE_CONSTANTS.FILE_FORMAT}
                      </span> <br /><span className="allowed-file-types">
                        {FILE_EXTENSION}
                      </span>
                    </p>
                    <p className="file-upload-text his-file-count-dragger">
                      {FILE_EXCEED_MESSAGE}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-element flex-row justify-center">
                <div className="flex-element justify-center items-center mr-10">
                  {(!fileList?.length && !isLoading) && (
                    <Button
                      className="patient-sign-primary-btn" type="primary"
                    >
                      <Plus_Icon />  <span className="patient-sign-modal-btn add-btn-text">Add / Upload more files</span>
                    </Button>
                  )}
                </div>
              </div>
            </Dragger>}
            {(fileList?.length > 0) && <div>
              <div
                onClick={(event: React.MouseEvent<HTMLDivElement>): void => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                className="patient-sign-file-upload-dragger"
              >
                <Row gutter={GUTTOR_SPACE} className="patient-sign-container">
                  <Col md={5} className="doc-label-text" />
                  <Col md={5} className="doc-text-patient-sign"><strong>Document</strong></Col>
                  <Col md={5} className="col-patient-sign-text"><strong>Uploaded on</strong></Col>
                  <Col md={3} className="his-doc-type pd-left-22 patient-sign-col-pd-r-0"><strong>Size</strong></Col>
                  <Col md={4} className="col-remove-text" />
                </Row>
                {renderUploadListItem(draggerActions, fileList)}
              </div>
            </div>}
          </div>
        </div>
      )}

      {form.getFieldValue('consentSignature') === PATIENT_SIGNATURE_CONSTANTS.PATIENT_SIGNATURE_OPTIONS[1] ? (
        <Form.Item>
          <div className="left-aligned-section">
            <p className="consent-disclaimer">
              {PATIENT_SIGNATURE_CONSTANTS.CONSENT_DISCLAIMER_TEXT}
            </p>
            <Col md={2} className="patient-consent-checkbox"><Checkbox
              checked={isChecked}
              onChange={handleCheckBox}
              className="patient-sign-upload-modal-icon patient-consent-checkbox"
            /></Col>
            <Col md={20}>&nbsp;{getPatientConsentDeclaration(ApplicationDetail?.instance?.getProviderName())}
            </Col>
          </div>
        </Form.Item>
      ) : (
        (fileList?.length > 0 && !isLoading) && (
          <div className="display-flex-item margin-top-37">
            <Checkbox
              checked={isChecked}
              disabled={!fileList?.length}
              onChange={handleCheckBox}
              data-testid="agreement" />
            {patientConsentAgreement(ApplicationDetail?.instance?.getProviderName())}
          </div>
        )
      )}

      {(showProceedButton && !isLoading) && (
        <div
          className={getConsentUploadButtonClass()}
        >
          <Button type="primary" className="consent-proceed-btn" htmlType="submit" disabled={!isChecked} onClick={onProceed}>
            Proceed
          </Button>
        </div>
      )}

    </Modal><PatientConsentSuccessModal showSuccessModal={showSuccessModal} setShowSuccessModal={setShowSuccessModal}
      setIsSubmitBtnVisible={setIsSubmitBtnVisible} setShowRandomOption={setShowRandomOption} /></>
  );
};

export default ConsentDeclarationModal;
